/**
 * Occasion greetings: a birthday, a US federal holiday, Teacher Appreciation
 * Day.
 *
 * Carried over from Cornerstone Jail, where they show full screen at sign-in.
 * Jail's public-safety remembrance days are swapped for a school occasion.
 * Each person controls their own in Preferences (birthday as MM/DD, and a
 * switch each for birthday and holiday greetings). A dismissed greeting stays
 * dismissed until midnight on this browser; it is a kindness, not a
 * notification, and should never come back twice in one day.
 */

import { useEffect, useState } from 'react'
import { useSession } from '@/lib/session'
import './greeting.css'

export interface Occasion {
  kind: 'birthday' | 'holiday'
  title: string
  message: string
}

const pad = (n: number) => String(n).padStart(2, '0')

/** The nth weekday (0 = Sunday) of a month; n = -1 for the last one. */
function nthWeekday(year: number, month: number, weekday: number, n: number): Date {
  if (n > 0) {
    const first = new Date(year, month, 1)
    const offset = (weekday - first.getDay() + 7) % 7
    return new Date(year, month, 1 + offset + (n - 1) * 7)
  }
  const last = new Date(year, month + 1, 0)
  const offset = (last.getDay() - weekday + 7) % 7
  return new Date(year, month, last.getDate() - offset)
}

/** Holidays for a year, keyed MM/DD. */
export function holidaysFor(year: number): Map<string, { title: string; message: string }> {
  const key = (d: Date) => `${pad(d.getMonth() + 1)}/${pad(d.getDate())}`
  const fixed = (month: number, day: number) => new Date(year, month, day)
  // Teacher Appreciation Day: Tuesday of the first full week of May.
  const firstMondayOfMay = nthWeekday(year, 4, 1, 1)
  const teacherDay = new Date(year, 4, firstMondayOfMay.getDate() + 1)

  const list: [Date, string, string][] = [
    [fixed(0, 1), "Happy New Year", 'Wishing you and your school a good year ahead.'],
    [nthWeekday(year, 0, 1, 3), 'Martin Luther King Jr. Day', 'A day to remember Dr. King and the work still ahead.'],
    [nthWeekday(year, 1, 1, 3), "Presidents' Day", "Honoring Washington's Birthday and the nation's presidents."],
    [nthWeekday(year, 4, 1, -1), 'Memorial Day', 'Remembering those who gave their lives in service.'],
    [fixed(5, 19), 'Juneteenth', 'Marking the end of slavery in the United States.'],
    [fixed(6, 4), 'Happy Independence Day', 'Enjoy the Fourth of July.'],
    [nthWeekday(year, 8, 1, 1), 'Labor Day', 'Thank you for the work you do all year.'],
    [nthWeekday(year, 9, 1, 2), "Columbus Day · Indigenous Peoples' Day", 'Recognizing the histories that shaped the Americas.'],
    [fixed(10, 11), 'Veterans Day', 'Thank you to everyone who has served.'],
    [nthWeekday(year, 10, 4, 4), 'Happy Thanksgiving', 'Wishing you a restful day with the people you care about.'],
    [fixed(11, 25), 'Merry Christmas', 'Wishing you a peaceful holiday.'],
    [teacherDay, 'Teacher Appreciation Day', 'Thank you for everything you do for your students.'],
  ]
  return new Map(list.map(([date, title, message]) => [key(date), { title, message }]))
}

export function occasionFor(
  date: Date,
  firstName: string,
  greetings: { birthday: string; showBirthday: boolean; showHolidays: boolean },
): Occasion | null {
  const today = `${pad(date.getMonth() + 1)}/${pad(date.getDate())}`
  if (greetings.showBirthday && greetings.birthday === today) {
    return {
      kind: 'birthday',
      title: `Happy birthday${firstName ? `, ${firstName}` : ''}!`,
      message: 'Everyone at Cornerstone hopes you have a wonderful day.',
    }
  }
  if (greetings.showHolidays) {
    const holiday = holidaysFor(date.getFullYear()).get(today)
    if (holiday) return { kind: 'holiday', ...holiday }
  }
  return null
}

const silenceKey = (userId: string, date: Date) =>
  `cs.edu.greeting.${userId}.${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

/** Shows today's greeting once, after sign-in, unless already dismissed today. */
export function OccasionGreeting() {
  const { session, preferences } = useSession()
  const [occasion, setOccasion] = useState<Occasion | null>(null)
  const userId = session?.user.id ?? ''
  const firstName = session?.user.firstName ?? ''

  useEffect(() => {
    // Never mid-view-as: the greeting would be for the person being inspected.
    if (!userId || session?.impersonatedBy) return
    const now = new Date()
    try {
      if (localStorage.getItem(silenceKey(userId, now))) return
    } catch {
      // Without storage it simply shows; dismissing still closes it.
    }
    setOccasion(occasionFor(now, firstName, preferences.greetings))
  }, [userId, firstName, preferences.greetings, session?.impersonatedBy])

  if (!occasion) return null

  function dismiss() {
    try {
      localStorage.setItem(silenceKey(userId, new Date()), '1')
    } catch {
      // Ignored: see above.
    }
    setOccasion(null)
  }

  return <GreetingOverlay occasion={occasion} onContinue={dismiss} />
}

export function GreetingOverlay({ occasion, onContinue }: { occasion: Occasion; onContinue: () => void }) {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onContinue()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onContinue])

  return (
    <div className={`greeting greeting--${occasion.kind}`} role="dialog" aria-modal="true" aria-labelledby="greeting-title">
      <div className="greeting__card">
        <p className="greeting__mark" aria-hidden="true">
          {occasion.kind === 'birthday' ? '🎂' : '★'}
        </p>
        <h2 id="greeting-title" className="greeting__title">
          {occasion.title}
        </h2>
        <p className="greeting__message">{occasion.message}</p>
        <button type="button" className="button button--primary" onClick={onContinue} autoFocus>
          Continue
        </button>
      </div>
    </div>
  )
}
